import {
  FILE_TYPE,
  FILE_TYPE_DISPLAY,
  type FileFormatScope,
  type FileTypeId,
} from '@steerfi/shared';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type React from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FileFormatPartySummary } from '@/api/file-formats';
import type { JsonEditorHandle } from '@/components/json-editor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { FileFormatCodeWarning } from './file-format-code-warning';
import type { FileFormatFormFieldErrors, FileFormatFormValues } from './file-format-form.types';
import { collectJsonbErrors, formatScopeSummary, JSONB_FIELDS } from './file-format-form.utils';
import { FileFormatJsonEditor } from './file-format-json-editor';
import { ScopePicker } from './scope-picker';

const FILE_TYPE_OPTIONS: ReadonlyArray<{ id: FileTypeId; label: string }> = (
  Object.values(FILE_TYPE) as FileTypeId[]
).map((id) => ({ id, label: FILE_TYPE_DISPLAY[id] }));

export type FileFormatFormHandle = {
  validateJsonEditors: () => boolean;
};

interface FileFormatFormProps {
  values: FileFormatFormValues;
  onChange: (values: FileFormatFormValues) => void;
  fieldErrors?: FileFormatFormFieldErrors;
  readOnlyScope?:
    | {
        scope: FileFormatScope;
        party?: FileFormatPartySummary | null;
        label?: 'locked' | 'read-only';
      }
    | undefined;
  initialCode?: string;
  /**
   * Reactive aggregation of every JSON editor's validity (true if all
   * editors are syntactically and schema-valid). Fires whenever the
   * combined state flips, so consumers can gate Save/Submit buttons.
   */
  onJsonValidityChange?: (allValid: boolean) => void;
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
    {children}
  </span>
);

export const FileFormatForm = forwardRef<FileFormatFormHandle, FileFormatFormProps>(
  function FileFormatForm(
    { values, onChange, fieldErrors = {}, readOnlyScope, initialCode = '', onJsonValidityChange },
    ref
  ) {
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const jsonRefs = useRef<Record<string, JsonEditorHandle | null>>({});

    // Per-editor validity reported live by FileFormatJsonEditor. Editors
    // that are unmounted (advanced section collapsed) are absent from this
    // map and treated as valid -- their bad buffer is discarded on unmount,
    // so when they remount they re-init from the parent's last good `value`.
    const [editorValidity, setEditorValidity] = useState<Record<string, boolean>>({});

    useImperativeHandle(ref, () => ({
      // Validate every editor (don't short-circuit) so the user sees all
      // JSONB errors at once, then aggregate the result.
      validateJsonEditors: () =>
        JSONB_FIELDS.map(({ key }) => jsonRefs.current[key]?.validate() ?? true).every(Boolean),
    }));

    const patch = (partial: Partial<FileFormatFormValues>) => onChange({ ...values, ...partial });

    const showCodeWarning = initialCode.length > 0 && values.code !== initialCode;

    const allJsonValid = useMemo(
      () => JSONB_FIELDS.every(({ key }) => editorValidity[key] !== false),
      [editorValidity]
    );

    useEffect(() => {
      onJsonValidityChange?.(allJsonValid);
    }, [allJsonValid, onJsonValidityChange]);

    // Reset tracked validity when the advanced section collapses: the
    // editors unmount and their transient bad buffers are gone, so any
    // stale `false` flags would incorrectly keep the form disabled.
    // Inverted as an early-return guard so the side-effecting branch
    // reads as the effect's actual purpose, not as a fallthrough.
    useEffect(() => {
      if (advancedOpen) return;
      setEditorValidity({});
    }, [advancedOpen]);

    // Reactive JSONB errors derived directly from `values`, so a stored
    // blob whose shape no longer satisfies the current shared schema
    // surfaces the problem the moment the modal opens (instead of leaving
    // the user staring at a disabled Save button). `fieldErrors` is only
    // populated after a submit attempt, but the Save button is gated by
    // the same validity flag this map drives -- so submit can't run --
    // which is exactly why we can't rely on `fieldErrors` here.
    const jsonbErrors = useMemo(() => collectJsonbErrors(values), [values]);
    const hasJsonbError = Object.keys(jsonbErrors).length > 0;

    // Auto-expand Advanced when any JSONB field reports an error so the
    // offending editor (with its inline error message) is visible without
    // the user having to discover the collapsed section. Intentionally
    // one-way: once the section is open, keep it open even after the user
    // fixes the value -- collapsing it from under them would be jarring,
    // and the toggle button is still right there if they want to hide it.
    useEffect(() => {
      if (hasJsonbError) setAdvancedOpen(true);
    }, [hasJsonbError]);

    const handleEditorValidity = useCallback(
      (key: string) => (valid: boolean) => {
        setEditorValidity((prev) => (prev[key] === valid ? prev : { ...prev, [key]: valid }));
      },
      []
    );

    return (
      <div className="flex flex-col gap-6 py-2">
        <div className="flex flex-col gap-4">
          <SectionLabel>Basics</SectionLabel>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="ff-code">
                Code <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ff-code"
                value={values.code}
                onChange={(e) => patch({ code: e.target.value })}
                aria-invalid={Boolean(fieldErrors.code)}
              />
              {fieldErrors.code ? (
                <p className="text-sm text-destructive">{fieldErrors.code}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="ff-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ff-name"
                value={values.format_name}
                onChange={(e) => patch({ format_name: e.target.value })}
                aria-invalid={Boolean(fieldErrors.format_name)}
              />
              {fieldErrors.format_name ? (
                <p className="text-sm text-destructive">{fieldErrors.format_name}</p>
              ) : null}
            </div>
          </div>
          <FileFormatCodeWarning visible={showCodeWarning} />
          <div className="flex flex-col gap-2">
            <Label htmlFor="ff-file-type">File Type</Label>
            <Select
              value={String(values.file_type_id)}
              onValueChange={(v) => patch({ file_type_id: Number(v) })}
            >
              <SelectTrigger
                id="ff-file-type"
                className="w-full"
                aria-invalid={Boolean(fieldErrors.file_type_id)}
              >
                <SelectValue placeholder="Select file type" />
              </SelectTrigger>
              <SelectContent>
                {FILE_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.id} value={String(opt.id)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldErrors.file_type_id ? (
              <p className="text-sm text-destructive">{fieldErrors.file_type_id}</p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ff-version">Version</Label>
            <Input
              id="ff-version"
              type="number"
              min={1}
              value={values.version_number}
              onChange={(e) => patch({ version_number: Number(e.target.value) })}
              aria-invalid={Boolean(fieldErrors.version_number)}
            />
            {fieldErrors.version_number ? (
              <p className="text-sm text-destructive">{fieldErrors.version_number}</p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ff-description">Description</Label>
            <Textarea
              id="ff-description"
              placeholder="Optional description"
              value={values.description}
              onChange={(e) => patch({ description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ff-s3">S3 bucket name</Label>
            <Input
              id="ff-s3"
              placeholder="optional"
              value={values.s3_bucket_name}
              onChange={(e) => patch({ s3_bucket_name: e.target.value })}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <SectionLabel>Scope</SectionLabel>
          {readOnlyScope ? (
            <div className="rounded-md border bg-muted/40 px-3 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Scope ({readOnlyScope.label ?? 'read-only'})
              </p>
              <p className="mt-1 text-sm font-medium">
                {formatScopeSummary(readOnlyScope.scope, readOnlyScope.party)}
              </p>
              {readOnlyScope.label === 'locked' && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Scoped to this {readOnlyScope.scope === 'client' ? 'client' : 'provider'}
                </p>
              )}
            </div>
          ) : (
            <ScopePicker
              scope={values.scope}
              partyId={values.party_id}
              onScopeChange={(scope) => patch({ scope, party_id: null })}
              onPartySelect={(partyId) => patch({ party_id: partyId })}
              partyError={fieldErrors.party_id}
            />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="ghost"
            className="flex w-full items-center justify-start gap-2 px-0 hover:bg-transparent"
            onClick={() => setAdvancedOpen((o) => !o)}
          >
            {advancedOpen ? (
              <IconChevronDown className="h-4 w-4" />
            ) : (
              <IconChevronRight className="h-4 w-4" />
            )}
            <SectionLabel>Configuration (advanced)</SectionLabel>
          </Button>
          {advancedOpen ? (
            <div className="flex flex-col gap-6">
              {JSONB_FIELDS.map(({ key, label, schema, helper }) => {
                // Prefer the submit-time error (set after a failed click)
                // because it's the user's most recent intent; fall back to
                // the reactive Zod check derived from `values` so a stale
                // pre-loaded blob is flagged immediately on open.
                const message = fieldErrors[key] ?? jsonbErrors[key];
                return (
                  <div key={key} className="flex flex-col gap-1">
                    <FileFormatJsonEditor
                      ref={(handle) => {
                        if (handle) {
                          jsonRefs.current[key] = handle;
                        } else {
                          delete jsonRefs.current[key];
                        }
                      }}
                      label={label}
                      helperText={helper}
                      schema={schema}
                      value={values[key]}
                      onChange={(v) => patch({ [key]: v })}
                      onValidityChange={handleEditorValidity(key)}
                    />
                    {message ? <p className="text-sm text-destructive">{message}</p> : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {fieldErrors.form ? <p className="text-sm text-destructive">{fieldErrors.form}</p> : null}
      </div>
    );
  }
);

FileFormatForm.displayName = 'FileFormatForm';
